import { gql } from "@apollo/client";

export const CREATE_SHIFT_SCHEDULE = gql`
  mutation CreateShiftSchedule($input: CreateShiftInput!) {
    createShiftSchedule(input: $input) {
      shift {
        id
        status
      }
    }
  }
`;

export const FETCH_ALL_SHIFTS = gql`
  query FetchAllShifts(
    $limit: Int
    $offset: Int
    $cityId: Int
    $neighborhoodId: Int
    $status: ShiftStatus
    $startDate: ISO8601DateTime
    $endDate: ISO8601DateTime
    $order: String
    $search: String
    $shiftTemplateId: ID
    $activeNow: Boolean
  ) {
    fetchAllShifts(
      limit: $limit
      offset: $offset
      cityId: $cityId
      neighborhoodId: $neighborhoodId
      status: $status
      startDate: $startDate
      endDate: $endDate
      order: $order
      search: $search
      shiftTemplateId: $shiftTemplateId
      activeNow: $activeNow
    ) {
      items {
        id
        name
        startTime
        endTime
        requiredDrivers
        status
        shiftTemplate {
          id
          name
        }
        assignedDriversCount
        availableSlots
        city {
          id
          name
        }
        neighborhood {
          id
          name
        }
        createdAt
        updatedAt
      }
      totalCount
    }
  }
`;

export const FETCH_SHIFT = gql`
  query FetchShift($id: ID!) {
    fetchShift(id: $id) {
      id
      name
      startTime
      endTime
      requiredDrivers
      status
      durationHours
      startLatitude
      startLongitude
      shiftTemplate {
        id
        name
      }
      city {
        id
        name
      }
      neighborhood {
        id
        name
      }
      createdAt
      updatedAt
      shiftAssignments {
        id
        status
        checkedInAt
        courier {
          id
          firstName
          lastName
          mobileNumber
          latitude 
          longitude
          isOnline
          isBusy
        }
      }
      shiftDriverStats {
        courierId
        onlineHours
        acceptanceRate
        compliant
      }
    }
  }
`;

export const FETCH_ALL_SHIFT_TEMPLATES = gql`
  query FetchAllShiftTemplates(
    $limit: Int
    $offset: Int
    $cityId: Int
    $status: ShiftTemplateStatus
    $recurrenceType: ShiftRecurrenceType
    $search: String
  ) {
    fetchAllShiftTemplates(
      limit: $limit
      offset: $offset
      cityId: $cityId
      status: $status
      recurrenceType: $recurrenceType
      search: $search
    ) {
      items {
        id
        name
        startTime
        endTime
        requiredDrivers
        recurrenceType
        daysOfWeek
        daysOfWeekNames
        status
        specificDate
        validFrom
        validUntil
        createdAt
        updatedAt
        city {
          id
          name
        }
        neighborhood {
          id
          name
        }
        couriers {
          id
          firstName
          lastName
        }
      }
      totalCount
    }
  }
`;

export const FETCH_SHIFT_TEMPLATE = gql`
  query FetchShiftTemplate($id: ID!) {
    fetchShiftTemplate(id: $id) {
      id
      name
      startTime
      endTime
      requiredDrivers
      recurrenceType
      specificDate
      daysOfWeek
      validFrom
      validUntil
      excludeDates
      priority
      generateDaysAhead
      status
      city {
        id
        name
      }
      neighborhood {
        id
        name
      }
      couriers {
        id
        firstName
        lastName
      }
      createdAt
      updatedAt
      shifts {
        id
        startTime
        endTime
        status
      }
    }
  }
`;

export const UPDATE_SHIFT = gql`
  mutation UpdateShift($input: UpdateShiftInput!) {
    updateShift(input: $input) {
      shift {
        id
        name
        requiredDrivers
        endTime
        startTime
        status
      }
    }
  }
`;

export const ACTIVATE_SHIFT = gql`
  mutation ActivateShift($input: ActivateShiftInput!) {
    activateShift(input: $input) {
      shift {
        id
        status
      }
    }
  }
`;

export const CANCEL_SHIFT = gql`
  mutation CancelShift($input: CancelShiftInput!) {
    cancelShift(input: $input) {
      shift {
        id
        status
      }
    }
  }
`;

export const ASSIGN_COURIER_TO_SHIFT = gql`
  mutation AssignCourierToShift($input: AssignCourierToShiftInput!) {
    assignCourierToShift(input: $input) {
      shiftAssignment {
        id
        status
        courier {
          id
          firstName
        }
      }
    }
  }
`;

export const UNASSIGN_COURIER_FROM_SHIFT = gql`
  mutation UnassignCourierFromShift($input: UnassignCourierFromShiftInput!) {
    unassignCourierFromShift(input: $input) {
      success
    }
  }
`;

export const BULK_ASSIGN_COURIERS_TO_SHIFT = gql`
  mutation BulkAssignCouriersToShift($input: BulkAssignCouriersToShiftInput!) {
    bulkAssignCouriersToShift(input: $input) {
      shift {
        id
        assignedDriversCount
      }
      successfulAssignments
      failedAssignments
      errors
    }
  }
`;

export const CREATE_SHIFT_TEMPLATE = gql`
  mutation CreateShiftTemplate($input: CreateShiftTemplateInput!) {
    createShiftTemplate(input: $input) {
      shiftTemplate {
        id
        name
        daysOfWeekNames
      }
    }
  }
`;

export const UPDATE_SHIFT_TEMPLATE = gql`
  mutation UpdateShiftTemplate($input: UpdateShiftTemplateInput!) {
    updateShiftTemplate(input: $input) {
      shiftTemplate {
        id
        requiredDrivers
        status
      }
    }
  }
`;

export const DELETE_SHIFT_TEMPLATE = gql`
  mutation DeleteShiftTemplate($input: DeleteShiftTemplateInput!) {
    deleteShiftTemplate(input: $input) {
      success
    }
  }
`;

export const ASSIGN_COURIER_TO_TEMPLATE = gql`
  mutation AssignCourierToTemplate($input: AssignCourierToTemplateInput!) {
    assignCourierToTemplate(input: $input) {
      shiftTemplate {
        couriers {
          id
          firstName
        }
      }
    }
  }
`;

export const UNASSIGN_COURIER_FROM_TEMPLATE = gql`
  mutation UnassignCourierFromTemplate($input: UnassignCourierFromTemplateInput!) {
    unassignCourierFromTemplate(input: $input) {
      success
    }
  }
`;

export const GENERATE_SHIFTS_FROM_TEMPLATES = gql`
  mutation GenerateShiftsFromTemplates($input: GenerateShiftsFromTemplatesInput!) {
    generateShiftsFromTemplates(input: $input) {
      createdCount
      skippedCount
      errors
    }
  }
`;

export const FETCH_COURIER_SHIFT_HISTORY = gql`
  query FetchCourierShiftHistory(
    $courierId: ID
    $supervisorIds: [ID!]
    $limit: Int
    $offset: Int
    $startDate: ISO8601DateTime
    $endDate: ISO8601DateTime
  ) {
    fetchCourierShiftHistory(
      courierId: $courierId
      supervisorIds: $supervisorIds
      limit: $limit
      offset: $offset
      startDate: $startDate
      endDate: $endDate
    ) {
      items {
        id
        shiftId
        courierId
        onlineHours
        busyHours
        idleHours
        onlineSeconds
        busySeconds
        idleSeconds
        onlinePercentage
        acceptanceRate
        acceptanceRateCompliant
        onlineHoursCompliant
        compliant
        notificationsReceived
        ordersAccepted
        ordersRejected
        createdAt
        updatedAt
        courier {
          id
          firstName
          lastName
          mobileNumber
          avatarUrl
          courierType
          city { id name }
        }
        shift {
          id
          startTime
          endTime
          status
          shiftTemplate {
            id
            name
          }
        }
      }
      totalCount
      totalOnlineHours
      totalBusyHours
      totalIdleHours
      averageAcceptanceRate
      compliantShiftsCount
    }
  }
`;

export const FETCH_ALL_COURIERS = gql`
  query FetchAllCouriers($limit: Int, $offset: Int, $query: String) {
    fetchAllCouriers(limit: $limit, offset: $offset, query: $query) {
      totalCount
      items {
        id
        isBusy
        isOnline
        avatarUrl
        firstName
        lastName
        lastSeen
        mobileNumber
        longitude
        latitude
        isBanned
        rating
        vehicleType
      }
    }
  }
`;

export const FETCH_ALL_SUPERVISORS = gql`
  query FetchAllSupervisors($query: String, $limit: Int = 20, $offset: Int) {
    fetchAllSupervisors(query: $query, limit: $limit, offset: $offset) {
      items {
        id
        name
        email
      }
      totalCount
    }
  }
`;

